// BOGO engine — port of Android `OrderCreateViewModel.recomputeAllBogoDeals` (the unified applier)
// at **INTERPRETATION A + ADD-ORDER MEMBERSHIP** (Android 2026-08-08), plus the QV-7124 dezi flip.
//
// ── SELECTION ───────────────────────────────────────────────────────────────────────────────────
//  • A deal "set" is **`buyQty` units**. The owner's model: "Buy 5 Get 2" = *within every 5 units,
//    the 2 cheapest are discounted* — NOT 5 paid plus 2 free.
//  • `eligible` is sorted by **`orderId` ASC (add-order)** and carved into contiguous sets; within
//    each FIRED set the `min(freeQty, buyQty)` **cheapest** members are discounted (ties → earliest).
//  • `fires = min(floor(N / buyQty), limit)`; blank/0/negative limit = UNLIMITED.
//  • Leftover units below a full set stay PLAIN.
//
// ⚠️ THE COMMIT-LOCK IS GONE (Android retired it on 2026-08-08, and so does this port). Add-order
// membership makes the freeze structural rather than stateful: a later-added unit always lands in a
// LATER set, so it can never steal a fired set's discounted slot — which is the bug the lock existed
// to prevent, and the bug the old *global-cheapest* re-selection re-introduced every recompute (add a
// cheaper unit after a set fired and the FREE badge visibly moved to it). Deletes now re-derive
// statelessly. `isCommittedBogo` survives only as a vestigial field: never set, cleared where found,
// so a sale HELD by an older build restores clean.
//
// History: cheapest-first → earliest → latest → Design 2 (latest + commit-lock) → cheapest-at-fire +
// commit-lock → Interpretation B (set = buyQty+freeQty, reverted on-device: "Buy 5 Get 2" then needed
// 7 units to fire) → **Interpretation A + add-order membership (current)**. Selection has flipped this
// often; do not "simplify" it without the owner.
//
// BOGO deals come from the Go delta bundle `bogos` (models.Bogo): id, buy_qty, free_qty, discount,
// discount_type ("1"=percent, "2"=flat), bogo_limit_per_order, is_disable ("0"=active), items (JSON of
// product/variant ids), schedule (start_date, end_date, no_end_date, full_day, start_time, end_time,
// repeat_type, weekly_days). Monthly repeat is NOT implemented (matches Android).
//
// TIMEZONE (spec O2): `now` is a JS Date read as local wall-clock; the caller passes a Date in the
// STORE timezone. Android uses device-local time.

import { roundHalfUp, toNum } from "./money";
import { bogoCapBinds, freshBogoFires, isBrokenPreservedBogoGroup, selectBogoDiscountedIndices } from "./promoHelpers";
import { isDeziLine } from "./dezi";

export function applyDiscount(price, type, value) {
  const p = toNum(price);
  let r;
  if (type === "1") r = p - (p * toNum(value)) / 100; // percent
  else if (type === "2") r = p - toNum(value); // flat
  else r = p;
  return Math.max(roundHalfUp(r), 0);
}

function parseTime(str) {
  if (!str) return null;
  const parts = String(str).split(":");
  if (parts.length < 2) return null;
  return toNum(parts[0]) * 60 + toNum(parts[1]);
}

function ymd(d) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]; // getDay(): 0=Sun

export function isBogoValidNow(bogo, now = new Date()) {
  const today = ymd(now);
  // 1) date range (ISO YYYY-MM-DD strings compare lexicographically)
  if (!bogo.start_date) return false;
  if (today < bogo.start_date) return false;
  if (bogo.no_end_date === "0") {
    if (!bogo.end_date) return false;
    if (today > bogo.end_date) return false;
  }
  // 2) time range
  if (bogo.full_day === "0") {
    const t0 = parseTime(bogo.start_time);
    const t1 = parseTime(bogo.end_time);
    if (t0 == null || t1 == null) return false;
    const cur = now.getHours() * 60 + now.getMinutes();
    const inRange = t0 <= t1 ? cur >= t0 && cur <= t1 : cur >= t0 || cur <= t1; // overnight span
    if (!inRange) return false;
  }
  // 3) weekly repeat
  if ((bogo.repeat_type || "").toLowerCase() === "0") {
    const allowed = String(bogo.weekly_days || "")
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    const todayShort = DAY_SHORT[now.getDay()].toLowerCase();
    if (allowed.length === 0 || !allowed.includes(todayShort)) return false;
  }
  return true;
}

/**
 * The ids a deal applies to — a faithful port of Android `getJsonDecoderProductList`.
 *
 * `items` is a JSON object keyed by PRODUCT id whose value is the list of VARIANT ids the deal covers:
 *   {"9314685":["3109162","3109163"]}   ← two specific variants
 *   {"9314683":[""]}                    ← the whole product (it has no variants)
 *
 * ⚠️ THE EMPTY LIST MEANS "THE PRODUCT ITSELF", AND THE KEY IS THE ID. Reading only the values — as
 * this did — silently dropped every product-level entry and added a useless `""` instead, so a deal
 * built on a non-variant product NEVER fired on the web while it fired on Android. Found live on Vape
 * Store deal 5699 ("bogo web"), where the $69.99 Mac half of the deal was simply inert.
 *
 * Android's exact rule: value missing / not an array / empty / first element empty ⇒ take the KEY,
 * otherwise take each element. Empty ids are never added (they cannot identify a line, and an id of
 * "" in this set would false-match any line with a blank match id).
 */
export function parseItemIds(items) {
  const set = new Set();
  if (!items) return set;
  let parsed;
  try {
    parsed = typeof items === "string" ? JSON.parse(items) : items;
  } catch {
    return set;
  }
  const add = (v) => {
    const s = v == null ? "" : String(v);
    if (s) set.add(s);
  };
  // A bare array of ids is not a shape Android accepts (JSONObject would throw), but it has always
  // been tolerated here and costs nothing to keep.
  if (Array.isArray(parsed)) {
    parsed.forEach(add);
    return set;
  }
  if (parsed && typeof parsed === "object") {
    for (const [key, value] of Object.entries(parsed)) {
      if (!Array.isArray(value) || value.length === 0 || String(value[0] ?? "") === "") add(key);
      else value.forEach(add);
    }
  }
  return set;
}

function lineMatchId(line) {
  return String(line.isVariant ? line.variantId : line.orderProductId);
}

/**
 * Restore a line to its raw, variant-aware catalog price and clear every BOGO flag.
 *
 * ⚠️ This is the ONLY restore path for a RESTORE-preserved set — those lines are skipped by
 * `resetWorkingPrices`, precisely so a held sale keeps its backend pairing — so it must be able to
 * recover the raw price on its own. `catalogUnitPrice` is the line's own list price (the VARIANT's
 * for a variant); the model fallback mirrors Android's `if (isvarient) vPrice else price` for lines
 * that predate it.
 */
function revertToRaw(l) {
  const m = l.model || {};
  const modelPrice = toNum(String(m.isvarient) === "1" ? m.vPrice : m.price);
  if (l.catalogUnitPrice != null) l.orderUnitPrice = toNum(l.catalogUnitPrice);
  else if (modelPrice > 0) l.orderUnitPrice = modelPrice;
  l.orderUnitNewPrice = toNum(l.manualOverridePrice);
  l.isBoGoProduct = false;
  l.bogoIdentifier = 0;
  l.bogoDealId = null;
  l.bogoDealName = "";
  l.bogoAltriaFunded = "";
  l.bogoPromotionCode = "";
  l.isWarningShow = "";
}

/**
 * QV-7124 Wave 6 Finding A — DEZI WINS, including over a RESTORE-preserved set.
 *
 * A restored BOGO line bypasses BOTH the reset (guarded by the preserve flag) and the eligibility
 * filter (`bogoIdentifier != 0`), so the dezi exclusion below cannot unwind it on its own — its BOGO
 * discount would survive alongside the manufacturer discount (stacking, real money). Force-revert any
 * dezi-upc line carrying BOGO state, regardless of the preserve flag.
 *
 * ONLY dezi-upc BOGO lines are touched: a dezi line with just a price override
 * (`bogoIdentifier === 0 && !isBoGoProduct`) is preserved, and non-dezi restored sets are untouched.
 * Empty dezi set ⇒ no-op ⇒ byte-identical.
 */
export function revertBogoOnDeziUpcs(cart, deziUpcSet) {
  if (!deziUpcSet || deziUpcSet.size === 0) return cart;
  for (const l of cart) {
    if (isDeziLine(l, deziUpcSet) && (toNum(l.bogoIdentifier) !== 0 || l.isBoGoProduct)) {
      revertToRaw(l);
      l.isCommittedBogo = false;
      l.isRestoredBogo = false;
    }
  }
  return cart;
}

/**
 * Re-derive every BOGO deal across the whole cart.
 *
 * @param cart        cart lines (mutated in place)
 * @param bogoList    the Go `bogos` bundle
 * @param now         Date in store timezone (schedule check)
 * @param deziUpcSet  Set of dezi upcs — those lines are excluded from eligibility (rule 1)
 */
export function applyBogoDeals(cart, bogoList, now = new Date(), deziUpcSet = null) {
  // 1️⃣ gift/custom rows never take part in a deal.
  for (const l of cart) {
    if (l.isGiftCard || l.isCustomLineItem) {
      l.isWarningShow = "";
      l.isBoGoProduct = false;
      l.bogoIdentifier = 0;
    }
  }

  // 2.4️⃣ UNFREEZE broken preserved groups (Android Part D). Restored lines are skipped by the reset
  // AND by eligibility on every pass, so deleting a set's PAID PARTNER used to strand the surviving
  // free line at its discounted price with no qualifying set — a checkout UNDERCHARGE. Members of one
  // preserved group share ONE identifier, so a group can span several fired sets;
  // isBrokenPreservedBogoGroup tests "enough paid partners for the free lines that remain".
  // Only `isRestoredBogo` preserves now — the live commit-lock is retired (see the file header).
  const groups = new Map();
  for (const l of cart) {
    if (l.isRestoredBogo && toNum(l.bogoIdentifier) !== 0) {
      const k = String(l.bogoIdentifier);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(l);
    }
  }
  for (const members of groups.values()) {
    const deal = (bogoList || []).find((b) => b && String(b.id) === String(members[0].bogoDealId));
    if (!deal) continue; // deal unknown ⇒ conservative: stay preserved
    const freeLines = members.filter((m) => m.isBoGoProduct).length;
    const broken = isBrokenPreservedBogoGroup(
      members.length,
      freeLines,
      toNum(deal.buy_qty),
      toNum(deal.free_qty)
    );
    if (broken) {
      for (const m of members) {
        m.isRestoredBogo = false;
        m.isCommittedBogo = false; // vestigial; cleared so a sale held by an older build cannot stay frozen
      }
    }
  }

  // 2.45️⃣ dezi wins, even over a committed/restored set.
  revertBogoOnDeziUpcs(cart, deziUpcSet);

  // 2.5️⃣ FULL RESET of every BOGO-consumed line back to raw so the fire count is recomputed over the
  // whole eligible cart in ONE pass. Excluded (untouched): gift/custom · restore-preserved ·
  // price-override lines · lines carrying a coupon/manual discount. Mix&Match lines are never BOGO
  // lines, so they never match the first clause.
  // (The Design-2 commit-lock exclusion is GONE: every non-restored BOGO line resets on every pass and
  //  the deal re-derives statelessly from add-order.)
  for (const l of cart) {
    const isBogoLine = toNum(l.bogoIdentifier) !== 0 || l.isBoGoProduct;
    if (!isBogoLine) continue;
    if (l.isGiftCard || l.isCustomLineItem) continue;
    if (l.isRestoredBogo) continue;
    if (toNum(l.manualOverridePrice) > 0) continue;
    if (toNum(l.orderDiscount) !== 0 || toNum(l.orderDiscountPercent) !== 0) continue;
    revertToRaw(l);
  }
  // Clear the promo HINT on every non-preserved line before the deal loop re-derives it. Android
  // clears it only on lines that were BOGO-consumed, which strands the hint on an eligible-but-unfired
  // line whose deal later expires or is disabled. Preserved lines keep the label they fired with (they
  // are excluded from `eligible`, so nothing would re-set it). Display only — no money is read here.
  for (const l of cart) {
    if (!l.isRestoredBogo && !l.isBoGoProduct) l.isWarningShow = "";
  }

  // 3️⃣ apply each active, in-schedule deal.
  let setSeq = 1;
  for (const bogo of bogoList || []) {
    if (!bogo || bogo.is_disable !== "0") continue;
    if (!isBogoValidNow(bogo, now)) continue;
    const itemIds = parseItemIds(bogo.items);
    if (itemIds.size === 0) continue;

    const buyQty = toNum(bogo.buy_qty);
    const freeQty = toNum(bogo.free_qty);
    if (buyQty <= 0 || freeQty <= 0) continue; // malformed / no-op deal (also guards divide-by-zero)

    // 4️⃣ eligible = matched, unclaimed, undiscounted, non-dezi — in ADD-ORDER (`orderId` ASC), which
    // is what makes set membership sticky: sets are contiguous blocks of this list, so a unit added
    // later is always in a later set. `orderId` is monotonic on both live adds and restore.
    const eligible = cart
      .filter(
        (l) =>
          itemIds.has(lineMatchId(l)) &&
          toNum(l.bogoIdentifier) === 0 &&
          !l.isSplitPaymentDiscount &&
          !l.isSplitPayment &&
          !l.isSplitRefund &&
          !l.isCustomLineItem &&
          !l.isGiftCard &&
          !isDeziLine(l, deziUpcSet) && // rule 1: a dezi upc never fires BOGO
          toNum(l.orderDiscount) === 0 &&
          toNum(l.orderDiscountPercent) === 0 &&
          toNum(l.orderUnitNewPrice) === 0
      )
      .sort((a, b) => toNum(a.orderId) - toNum(b.orderId));

    // PRESERVE-AWARE CAP — count fires already preserved this order for THIS deal (restored via a
    // hold) so preserved + fresh are capped TOGETHER. Without this, restoring a capped order
    // re-grants the fires the cap had suppressed (a double discount).
    const preservedFires = Math.floor(
      cart.filter((l) => l.isRestoredBogo && l.isBoGoProduct && String(l.bogoDealId) === String(bogo.id)).length / freeQty
    );
    const totalSets = freshBogoFires(eligible.length, buyQty, bogo.bogo_limit_per_order, preservedFires);
    // Has the limit bound? LABEL-ONLY now (the hint below); it locks nothing.
    const capBinds = bogoCapBinds(preservedFires, totalSets, bogo.bogo_limit_per_order);
    // Which add-order positions get the discount: min(freeQty, buyQty) cheapest WITHIN each fired set.
    // Delegated to the pure helper so this and its unit tests share one code path, as Android does.
    const discountedIdx = selectBogoDiscountedIndices(
      eligible.map((l) => toNum(l.orderUnitPrice)),
      buyQty,
      freeQty,
      bogo.bogo_limit_per_order,
      preservedFires
    );

    for (let s = 0; s < totalSets; s++) {
      const start = s * buyQty;
      const end = (s + 1) * buyQty;
      if (end > eligible.length) break;
      const setId = Date.now() + setSeq++;
      for (let i = start; i < end; i++) {
        const it = eligible[i];
        // Every member carries the set identity + the deal label; the paying partners keep their price.
        it.bogoIdentifier = setId;
        it.bogoDealId = bogo.id;
        it.bogoDealName = bogo.deal_name || ""; // for altria_discount_desc
        it.bogoAltriaFunded = bogo.altria_funded_discount || "";
        // Altria LFP promotion code (AGDC scan field 29). The backend stores one only on an
        // Altria-funded deal, so its presence is itself the "this is an LFP offer" signal.
        it.bogoPromotionCode = bogo.promotion_code || "";
        it.isWarningShow = bogo.description || "";
        it.isBoGoProduct = false;
        if (discountedIdx.has(i)) {
          // Android parity: overwrite the WORKING orderUnitPrice too, so a 100%-off unit reads as 0 in
          // the subtotal. The immutable base is catalogUnitPrice, restored before every pass.
          const newPrice = applyDiscount(it.orderUnitPrice, bogo.discount_type, toNum(bogo.discount));
          it.orderUnitNewPrice = newPrice;
          it.orderUnitPrice = newPrice;
          it.isBoGoProduct = true;
        }
      }
    }

    // LABEL PARITY, narrowed. The deal description on an eligible-but-unfired line is an "add one
    // more and it fires" HINT. Once the per-order limit has BOUND those lines stay full price for
    // this order, so keeping "Buy 1, Get 1 …" on them was simply wrong (the reported bug). Clear only
    // OUR text, so a label another deal put on the line survives. LABEL ONLY — no price change.
    const unfired = eligible.filter((l) => toNum(l.bogoIdentifier) === 0);
    const desc = bogo.description || "";
    if (capBinds) {
      for (const l of unfired) if (l.isWarningShow === desc) l.isWarningShow = "";
    } else {
      for (const l of unfired) l.isWarningShow = desc;
    }
  }
  return cart;
}
